import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { furnitureService, FurnitureData, FurnitureFilters } from '../../services/furniture';
import { MVP_ENABLE_CHECKOUT } from '../../constants/mvp';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { savedService } from '../../services/saved';
import { useTranslation } from 'react-i18next';

interface FurnitureListProps {
  filters: FurnitureFilters;
  isSeller?: boolean;
}

const FurnitureList: React.FC<FurnitureListProps> = ({ 
  filters,
  isSeller = false
}) => {
  const [furniture, setFurniture] = useState<FurnitureData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [savedItems, setSavedItems] = useState<string[]>([]);
  const [savingItems, setSavingItems] = useState<string[]>([]);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (user) savedService.list().then(items => setSavedItems(items.map(item => item._id))).catch(console.error);
    else setSavedItems([]);
  }, [user]);

  const handleSaveItem = useCallback(async (item: FurnitureData) => {
    const itemId = item._id;
    if (!user) {
      navigate('/login', { state: { from: location } });
      return;
    }
    setSavingItems(prev => [...prev, itemId]);
    try {
      if (savedItems.includes(itemId)) {
        await savedService.remove(itemId);
        setSavedItems(prev => prev.filter(id => id !== itemId));
      } else {
        await savedService.save(itemId);
        setSavedItems(prev => [...prev, itemId]);
      }
    } catch (error) {
      console.error('Failed to save listing', error);
    } finally {
      setSavingItems(prev => prev.filter(id => id !== itemId));
    }
  }, [savedItems, user, navigate, location]);

  const fetchFurniture = useCallback(async () => {
    try {
      setLoading(true);
      const response = isSeller
        ? await furnitureService.getSellerListings(filters.status, filters.page, filters.limit)
        : await furnitureService.getFurniture(filters);

      setFurniture(response.furniture);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      setError('furniture.listing.loadError');
    } finally {
      setLoading(false);
    }
  }, [filters, isSeller]);

  useEffect(() => {
    fetchFurniture();
  }, [fetchFurniture]);

  const handlePageChange = (newPage: number) => {
    // Since we can't directly modify props, we'll emit an event that the parent can handle
    // In a real application, you'd use a callback prop or context for this
    const event = new CustomEvent('pageChange', { detail: { page: newPage } });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {t(error)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isSeller && (
        <div className="flex justify-end">
          <Link
            to="/furniture/new"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('seller.dashboard.addListing')}
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {furniture.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">{t('furniture.listing.noResults')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('furniture.listing.noResultsHint')}</p>
          </div>
        ) : (
          furniture.map(item => (
            <div
              key={item._id}
              className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {item.images && item.images[0] && (
                <img
                  src={item.images[0]}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{item.description.slice(0, 100)}...</p>
                <div className="mt-2 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">${item.price}</span>
                  <span className="text-sm text-gray-500 capitalize">{t(`furniture.condition.${item.condition}`)}</span>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  {item.location.city}, {item.location.state}
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <Link
                      to={`/furniture/${item._id}`}
                      className="text-indigo-600 hover:text-indigo-500 font-medium"
                    >
                      {t('furniture.listing.viewDetails')}
                    </Link>
                    
                    {!isSeller && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleSaveItem(item);
                        }}
                        disabled={savingItems.includes(item._id)}
                        className={`p-2 rounded-full transition-colors disabled:opacity-50 ${
                          savedItems.includes(item._id)
                            ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                        }`}
                        title={t(savedItems.includes(item._id) ? 'furniture.listing.removeSaved' : 'furniture.listing.saveItem')}
                      >
                        <svg 
                          className={`w-5 h-5 ${savingItems.includes(item._id) ? 'animate-pulse' : ''}`}
                          fill={savedItems.includes(item._id) ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  {MVP_ENABLE_CHECKOUT && !isSeller && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        addToCart({
                          id: item._id,
                          title: item.title,
                          price: item.price,
                          images: item.images,
                          condition: item.condition,
                          category: item.category,
                          location: item.location
                        });
                      }}
                      className="w-full bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      {t('furniture.listing.addToCart')}
                    </button>
                  )}
                  {!MVP_ENABLE_CHECKOUT && (
                    <Link
                      to={`/furniture/${item._id}`}
                      className="block w-full text-center bg-indigo-600 text-white py-2 px-3 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium"
                    >
                      {t('furniture.listing.viewDetails')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center space-x-2 mt-6">
          <button
            onClick={() => handlePageChange(filters.page! - 1)}
            disabled={filters.page === 1}
            className="px-3 py-1 border rounded-md disabled:opacity-50"
          >
            {t('furniture.listing.prevPage')}
          </button>
          <span className="px-3 py-1">
            {t('furniture.listing.pageOf', { page: filters.page, total: totalPages })}
          </span>
          <button
            onClick={() => handlePageChange(filters.page! + 1)}
            disabled={filters.page === totalPages}
            className="px-3 py-1 border rounded-md disabled:opacity-50"
          >
            {t('furniture.listing.nextPage')}
          </button>
        </div>
      )}
    </div>
  );
};

export default FurnitureList;
